import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { 
    getB2bPackagingOrders, 
    generateB2bOrderLabel 
} from '../../services/labelInvoice';
import { ProductSearchInput } from './LabelInvoiceGenerator';
import ShowModal from '../../utils/ShowModal';
import { sanitizeMobile } from '../../utils/phone';
import DateRangePicker from '../../components/inputFields/DateRangePicker';

// ── Helpers ──
const getFirstDayOfMonth = () => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
};

const getToday = () => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const Spinner = ({ className = "h-4 w-4 border-white" }) => (
    <div className={`animate-spin rounded-full border-b-2 ${className}`}></div>
);

const EMPTY_B2B_LABEL = {
    customer_name: '',
    shop_name: '',
    mobile_number: '',
    alternate_number: '',
    transport_name: '',
    transport_number: '',
    note: '',
    dispatch_instruction: '',
    items: [{ name: '', quantity: 1, note: '' }]
};

const generateTempId = (orderId = 'custom') => 
    `${orderId}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

export default function B2BPackagingLabels() {
    // ── Filtering and List State ──
    const [startDate, setStartDate] = useState(getFirstDayOfMonth());
    const [endDate, setEndDate] = useState(getToday());
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());

    // ── Modal / Editor States ──
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editingOrder, setEditingOrder] = useState({ ...EMPTY_B2B_LABEL });
    const [isBatchOpen, setIsBatchOpen] = useState(false);
    const [batchOrders, setBatchOrders] = useState([]);
    const [batchExpandedIndex, setBatchExpandedIndex] = useState(0);
    const [printingSingle, setPrintingSingle] = useState(false);
    const [printingBatch, setPrintingBatch] = useState(false);

    // ── Fetch List ──
    const fetchB2bOrders = useCallback(async () => {
        if (!startDate || !endDate) return;
        if (startDate > endDate) {
            toast.error("Start date cannot be after End date");
            return;
        }
        setLoading(true);
        setError('');
        try {
            const response = await getB2bPackagingOrders({
                start_date: startDate,
                end_date: endDate
            });
            const fetchedOrders = response.data?.orders || response.data || [];
            setOrders(Array.isArray(fetchedOrders) ? fetchedOrders : []);
        } catch (err) {
            console.error("Error fetching B2B packaging orders:", err);
            const msg = err?.response?.data?.error || err?.response?.data?.message || 'Failed to load B2B orders.';
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    const handleRangeChange = ({ start, end }) => {
        if (start && end) {
            if (start > end) return;
            setStartDate(start);
            setEndDate(end);
            return;
        }
        if (!start && !end) {
            setStartDate(getFirstDayOfMonth());
            setEndDate(getToday());
        }
    };

    useEffect(() => {
        fetchB2bOrders();
    }, [fetchB2bOrders]);

    // ── Checkbox Handlers ──
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedOrderIds(new Set(orders.map(o => o.order_id).filter(Boolean)));
        } else {
            setSelectedOrderIds(new Set());
        }
    };

    const handleSelectRow = (orderId, e) => {
        e.stopPropagation(); // Avoid triggering row-click edit modal
        const newSet = new Set(selectedOrderIds);
        if (newSet.has(orderId)) {
            newSet.delete(orderId);
        } else {
            newSet.add(orderId);
        }
        setSelectedOrderIds(newSet);
    };

    // ── Single Label Editor Logic ──
    const openEditor = (order = null) => {
        if (order) {
            // Edit existing order pre-filled
            const mappedItems = Array.isArray(order.items) 
                ? order.items.map(item => ({
                    name: item.name || item.product_name || '',
                    quantity: item.quantity || 1,
                    note: item.note || item.dispatch_note || ''
                  }))
                : [{ name: '', quantity: 1, note: '' }];

            setEditingOrder({
                order_id: order.order_id || '',
                customer_name: order.customer_name || '',
                shop_name: order.shop_name || '',
                mobile_number: order.mobile_number || '',
                alternate_number: order.alternate_number || '',
                transport_name: order.transport_name || '',
                transport_number: order.transport_number || '',
                note: order.note || '',
                dispatch_instruction: order.dispatch_instruction || '',
                items: mappedItems
            });
        } else {
            // Custom label (order-less)
            setEditingOrder({
                ...EMPTY_B2B_LABEL,
                items: [{ name: '', quantity: 1, note: '' }]
            });
        }
        setIsEditorOpen(true);
    };

    const handleFieldChange = (field, value) => {
        if (field === 'mobile_number' || field === 'alternate_number') {
            value = sanitizeMobile(value);
        }
        setEditingOrder(prev => ({ ...prev, [field]: value }));
    };

    const handleItemChange = (index, field, value) => {
        setEditingOrder(prev => {
            const items = [...prev.items];
            items[index] = { ...items[index], [field]: value };
            return { ...prev, items };
        });
    };

    const handleAddItem = () => {
        setEditingOrder(prev => ({
            ...prev,
            items: [...prev.items, { name: '', quantity: 1, note: '' }]
        }));
    };

    const handleRemoveItem = (index) => {
        if (editingOrder.items.length <= 1) {
            toast.error("At least one item is required");
            return;
        }
        setEditingOrder(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    // Client-side validation helper
    const validateLabelState = (labelData) => {
        if (!labelData.customer_name?.trim()) {
            return "Customer name is required.";
        }
        const validItems = labelData.items?.filter(item => item.name?.trim());
        if (!validItems || validItems.length === 0) {
            return "At least one item with a valid name is required.";
        }
        return null;
    };

    const handleSinglePrint = async (e) => {
        e.preventDefault();
        const validationError = validateLabelState(editingOrder);
        if (validationError) {
            toast.error(validationError);
            return;
        }

        setPrintingSingle(true);
        try {
            // Format single POST payload
            const payload = {
                customer_name: editingOrder.customer_name.trim(),
                shop_name: editingOrder.shop_name?.trim() || '',
                mobile_number: editingOrder.mobile_number?.trim() || '',
                alternate_number: editingOrder.alternate_number?.trim() || '',
                transport_name: editingOrder.transport_name?.trim() || '',
                transport_number: editingOrder.transport_number?.trim() || '',
                note: editingOrder.note?.trim() || '',
                dispatch_instruction: editingOrder.dispatch_instruction?.trim() || '',
                items: editingOrder.items
                    .filter(item => item.name?.trim())
                    .map(item => ({
                        name: item.name.trim(),
                        quantity: Number(item.quantity) || 1,
                        note: item.note?.trim() || ''
                    }))
            };
            if (editingOrder.order_id) {
                payload.order_id = editingOrder.order_id;
            }

            const pdfBlob = await generateB2bOrderLabel(payload);
            const url = URL.createObjectURL(pdfBlob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            toast.success("PDF generated successfully");
            setIsEditorOpen(false);
            fetchB2bOrders(); // Refresh list to reflect updates/status shifts if applicable
        } catch (err) {
            console.error("Single print generation error:", err);
            toast.error(err.message || "Failed to generate label");
        } finally {
            setPrintingSingle(false);
        }
    };

    // ── Batch Flow Review & Accordion Logic ──
    const startBatchReview = () => {
        if (selectedOrderIds.size === 0) {
            toast.error("Please select at least one order to print");
            return;
        }
        // Build the initial batch review list pre-filled from selected orders
        const initialBatch = orders
            .filter(o => selectedOrderIds.has(o.order_id))
            .map(order => {
                const mappedItems = Array.isArray(order.items) 
                    ? order.items.map(item => ({
                        name: item.name || item.product_name || '',
                        quantity: item.quantity || 1,
                        note: item.note || item.dispatch_note || ''
                      }))
                    : [{ name: '', quantity: 1, note: '' }];

                return {
                    tempId: generateTempId(order.order_id),
                    order_id: order.order_id || '',
                    customer_name: order.customer_name || '',
                    shop_name: order.shop_name || '',
                    mobile_number: order.mobile_number || '',
                    alternate_number: order.alternate_number || '',
                    transport_name: order.transport_name || '',
                    transport_number: order.transport_number || '',
                    note: order.note || '',
                    dispatch_instruction: order.dispatch_instruction || '',
                    items: mappedItems
                };
            });

        setBatchOrders(initialBatch);
        setBatchExpandedIndex(0);
        setIsBatchOpen(true);
    };

    const handleBatchFieldChange = (orderIndex, field, value) => {
        setBatchOrders(prev => {
            const updated = [...prev];
            let processedValue = value;
            if (field === 'mobile_number' || field === 'alternate_number') {
                processedValue = sanitizeMobile(value);
            }
            updated[orderIndex] = { ...updated[orderIndex], [field]: processedValue };
            return updated;
        });
    };

    const handleBatchItemChange = (orderIndex, itemIndex, field, value) => {
        setBatchOrders(prev => {
            const updated = [...prev];
            const items = [...updated[orderIndex].items];
            items[itemIndex] = { ...items[itemIndex], [field]: value };
            updated[orderIndex] = { ...updated[orderIndex], items };
            return updated;
        });
    };

    const handleBatchAddItem = (orderIndex) => {
        setBatchOrders(prev => {
            const updated = [...prev];
            updated[orderIndex] = {
                ...updated[orderIndex],
                items: [...updated[orderIndex].items, { name: '', quantity: 1, note: '' }]
            };
            return updated;
        });
    };

    const handleBatchRemoveItem = (orderIndex, itemIndex) => {
        if (batchOrders[orderIndex].items.length <= 1) {
            toast.error("At least one item is required");
            return;
        }
        setBatchOrders(prev => {
            const updated = [...prev];
            updated[orderIndex] = {
                ...updated[orderIndex],
                items: updated[orderIndex].items.filter((_, i) => i !== itemIndex)
            };
            return updated;
        });
    };

    const handleDuplicateBatchOrder = (orderIndex, e) => {
        e.stopPropagation(); // Avoid expanding/collapsing accordion
        const target = batchOrders[orderIndex];
        const duplicate = {
            ...target,
            tempId: generateTempId(target.order_id),
            // Deep copy items array
            items: target.items.map(item => ({ ...item }))
        };
        setBatchOrders(prev => {
            const updated = [...prev];
            updated.splice(orderIndex + 1, 0, duplicate);
            return updated;
        });
        setBatchExpandedIndex(orderIndex + 1);
        toast.success("Parcel duplicated");
    };

    const handleRemoveBatchOrder = (orderIndex, e) => {
        e.stopPropagation(); // Avoid expanding/collapsing accordion
        if (batchOrders.length <= 1) {
            toast.error("Batch review must contain at least one label");
            return;
        }
        setBatchOrders(prev => prev.filter((_, i) => i !== orderIndex));
        setBatchExpandedIndex(prev => Math.max(0, prev - 1));
    };

    const handleBatchPrint = async () => {
        // Validate all batch orders
        for (let i = 0; i < batchOrders.length; i++) {
            const err = validateLabelState(batchOrders[i]);
            if (err) {
                toast.error(`Label #${i + 1} (${batchOrders[i].customer_name || 'Walk-in'}): ${err}`);
                setBatchExpandedIndex(i);
                return;
            }
        }

        setPrintingBatch(true);
        try {
            // Build body: { orders: [ ... ] }
            const ordersPayload = batchOrders.map(bo => {
                const label = {
                    customer_name: bo.customer_name.trim(),
                    shop_name: bo.shop_name?.trim() || '',
                    mobile_number: bo.mobile_number?.trim() || '',
                    alternate_number: bo.alternate_number?.trim() || '',
                    transport_name: bo.transport_name?.trim() || '',
                    transport_number: bo.transport_number?.trim() || '',
                    note: bo.note?.trim() || '',
                    dispatch_instruction: bo.dispatch_instruction?.trim() || '',
                    items: bo.items
                        .filter(item => item.name?.trim())
                        .map(item => ({
                            name: item.name.trim(),
                            quantity: Number(item.quantity) || 1,
                            note: item.note?.trim() || ''
                        }))
                };
                if (bo.order_id) {
                    label.order_id = bo.order_id;
                }
                return label;
            });

            const pdfBlob = await generateB2bOrderLabel({ orders: ordersPayload });
            const url = URL.createObjectURL(pdfBlob);
            
            // Open preview tab
            window.open(url, '_blank');

            // Trigger file download
            const link = document.createElement('a');
            link.href = url;
            link.download = `b2b_labels_${Date.now()}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => URL.revokeObjectURL(url), 60000);
            
            toast.success("Batch PDF generated and downloaded");
            setIsBatchOpen(false);
            setSelectedOrderIds(new Set()); // Clear selection upon success
            fetchB2bOrders();
        } catch (err) {
            console.error("Batch print generation error:", err);
            toast.error(err.message || "Failed to generate batch labels");
        } finally {
            setPrintingBatch(false);
        }
    };

    return (
        <div className="mt-5 space-y-5 text-left">
            {/* ── Filter Bar ── */}
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-col gap-4 p-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                        <h2 className="text-base font-semibold text-gray-900 text-left">B2B Packaging Labels</h2>
                        <p className="mt-1 text-xs text-gray-500 text-left">
                            Select B2B orders to generate transport labels in bulk, or generate a custom order-less label.
                        </p>
                    </div>
                    <div className="flex flex-wrap w-full gap-2 sm:flex-nowrap lg:w-auto">
                        <DateRangePicker
                            compact
                            value={{ start: startDate, end: endDate }}
                            onChange={handleRangeChange}
                            placeholder="Choose start & end date"
                        />
                        <button
                            type="button"
                            onClick={fetchB2bOrders}
                            disabled={loading}
                            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50"
                        >
                            {loading ? <Spinner className="h-4 w-4 border-gray-500" /> : <span className="text-sm">⟳</span>}
                            Refresh
                        </button>
                        <button
                            type="button"
                            onClick={() => openEditor(null)}
                            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-purple-700 shadow-sm"
                        >
                            <span>+</span> Custom Label
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            {/* ── Table / List Area ── */}
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                        <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                            <tr>
                                <th className="w-12 px-6 py-4 text-center">
                                    <input
                                        type="checkbox"
                                        checked={orders.length > 0 && selectedOrderIds.size === orders.filter(o => o.order_id).length}
                                        onChange={handleSelectAll}
                                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                    />
                                </th>
                                <th className="px-6 py-4 font-semibold">Date</th>
                                <th className="px-6 py-4 font-semibold">Customer</th>
                                <th className="px-6 py-4 font-semibold">Shop</th>
                                <th className="px-6 py-4 font-semibold">Transport</th>
                                <th className="px-6 py-4 font-semibold">Items</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading && (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                                        <div className="inline-flex items-center gap-2 justify-center">
                                            <Spinner className="h-5 w-5 border-blue-500" />
                                            <span>Loading B2B packaging orders...</span>
                                        </div>
                                    </td>
                                </tr>
                            )}

                            {!loading && orders.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-gray-400 font-medium italic">
                                        No B2B orders in packaging for this date range.
                                    </td>
                                </tr>
                            )}

                            {!loading && orders.map((order) => {
                                const orderId = order.order_id;
                                const isChecked = selectedOrderIds.has(orderId);
                                const hasTransport = !!order.transport_name;
                                const itemsCount = Array.isArray(order.items)
                                    ? order.items.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0)
                                    : 0;

                                return (
                                    <tr 
                                        key={orderId} 
                                        onClick={() => openEditor(order)}
                                        className="hover:bg-slate-50/80 cursor-pointer transition-colors"
                                    >
                                        <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={(e) => handleSelectRow(orderId, e)}
                                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                            />
                                        </td>
                                        <td className="px-6 py-4 font-medium text-gray-700">
                                            {order.date ? new Date(order.date).toLocaleDateString('en-IN', {
                                                day: '2-digit', month: 'short'
                                            }) : '—'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="font-semibold text-gray-900">{order.customer_name || '—'}</p>
                                            <p className="text-xs text-gray-500 mt-0.5">{order.mobile_number || '—'}</p>
                                        </td>
                                        <td className="px-6 py-4 text-gray-700 font-medium">
                                            {order.shop_name || <span className="text-gray-400 italic">—</span>}
                                        </td>
                                        <td className="px-6 py-4">
                                            {hasTransport ? (
                                                <div>
                                                    <p className="font-semibold text-gray-800">{order.transport_name}</p>
                                                    {order.transport_number && (
                                                        <p className="text-xs text-gray-500 mt-0.5">{order.transport_number}</p>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800 border border-amber-200/60 shadow-sm animate-pulse">
                                                    ⚠ No Transport
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-gray-900 font-semibold">
                                            {itemsCount} {itemsCount === 1 ? 'item' : 'items'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Bottom Bulk Actions Footer Bar ── */}
            {selectedOrderIds.size > 0 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-white border border-slate-200/80 rounded-2xl shadow-2xl py-3 px-6 flex items-center justify-between gap-6 min-w-[320px] sm:min-w-[450px] animate-slideUp">
                    <div className="flex flex-col">
                        <span className="text-xs text-gray-400 font-medium">Bulk Action</span>
                        <span className="text-sm font-bold text-gray-800">{selectedOrderIds.size} Selected</span>
                    </div>
                    <button
                        type="button"
                        onClick={startBatchReview}
                        className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-all shadow-md shadow-blue-200 flex items-center gap-1.5"
                    >
                        <span>Print Selected ({selectedOrderIds.size})</span>
                    </button>
                </div>
            )}

            {/* ═══════════════════════════════════════ */}
            {/* MODAL 1: SINGLE LABEL EDITOR          */}
            {/* ═══════════════════════════════════════ */}
            <ShowModal
                isOpen={isEditorOpen}
                onRequestClose={() => setIsEditorOpen(false)}
                title={editingOrder.order_id ? "Edit B2B Label Details" : "Create Custom B2B Label"}
                modalWidth="60%"
            >
                <form onSubmit={handleSinglePrint} className="space-y-6 pb-2 text-left">
                    {/* Basic Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-gray-600">Customer Name <span className="text-red-500">*</span></label>
                            <input 
                                type="text" 
                                value={editingOrder.customer_name} 
                                onChange={(e) => handleFieldChange('customer_name', e.target.value)}
                                placeholder="e.g. Rohit Patel" 
                                required
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" 
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-gray-600">Shop Name</label>
                            <input 
                                type="text" 
                                value={editingOrder.shop_name} 
                                onChange={(e) => handleFieldChange('shop_name', e.target.value)}
                                placeholder="e.g. Patel Agro Traders" 
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" 
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-gray-600">Mobile Number</label>
                            <input 
                                type="text" 
                                value={editingOrder.mobile_number} 
                                onChange={(e) => handleFieldChange('mobile_number', e.target.value)}
                                placeholder="e.g. 8823012601" 
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" 
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-gray-600">Alternate Number</label>
                            <input 
                                type="text" 
                                value={editingOrder.alternate_number} 
                                onChange={(e) => handleFieldChange('alternate_number', e.target.value)}
                                placeholder="e.g. 9998887776" 
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" 
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-gray-600">Transport Company Name</label>
                            <input 
                                type="text" 
                                value={editingOrder.transport_name} 
                                onChange={(e) => handleFieldChange('transport_name', e.target.value)}
                                placeholder="e.g. VRL Logistics" 
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" 
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-gray-600">Transport Contact Number</label>
                            <input 
                                type="text" 
                                value={editingOrder.transport_number} 
                                onChange={(e) => handleFieldChange('transport_number', e.target.value)}
                                placeholder="e.g. 9876543210" 
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" 
                            />
                        </div>
                    </div>

                    {/* Note & Dispatch Instruction */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-gray-600">Note (Order Level Note)</label>
                        <input 
                            type="text" 
                            value={editingOrder.note} 
                            onChange={(e) => handleFieldChange('note', e.target.value)}
                            placeholder="e.g. Deliver before 6 PM" 
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" 
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-gray-600">Dispatch Instructions (Free text, printed on label)</label>
                        <textarea 
                            value={editingOrder.dispatch_instruction} 
                            onChange={(e) => handleFieldChange('dispatch_instruction', e.target.value)}
                            placeholder="e.g. Pack insulators separately. Verify battery count." 
                            rows={2}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none" 
                        />
                    </div>

                    {/* Items Table */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between border-b pb-2 border-gray-200">
                            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Item Details</h3>
                            <button 
                                type="button" 
                                onClick={handleAddItem}
                                className="px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-semibold rounded-lg transition-colors border border-purple-200"
                            >
                                + Add Row
                            </button>
                        </div>

                        {/* Items Header */}
                        <div className="hidden md:grid grid-cols-12 gap-2 bg-gray-50 p-2 rounded-t-lg text-xs font-bold text-gray-500 uppercase tracking-wider">
                            <div className="col-span-6">Item Name</div>
                            <div className="col-span-2 text-center">Qty</div>
                            <div className="col-span-3">Item Packing Note</div>
                            <div className="col-span-1 text-center">✕</div>
                        </div>

                        <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                            {editingOrder.items.map((item, index) => (
                                <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center border border-gray-100 p-2 md:p-0 md:border-0 rounded-lg md:rounded-none">
                                    <div className="col-span-6">
                                        <ProductSearchInput 
                                            value={item.name} 
                                            placeholder="Item Name (search or type)..."
                                            onChange={(text) => handleItemChange(index, 'name', text)}
                                            onSelect={(prod) => handleItemChange(index, 'name', prod.product_name)} 
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <input 
                                            type="number" 
                                            min="1" 
                                            value={item.quantity} 
                                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                            className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-purple-500" 
                                        />
                                    </div>
                                    <div className="col-span-3">
                                        <input 
                                            type="text" 
                                            value={item.note} 
                                            onChange={(e) => handleItemChange(index, 'note', e.target.value)}
                                            placeholder="e.g. Fragile"
                                            className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" 
                                        />
                                    </div>
                                    <div className="col-span-1 text-center">
                                        {editingOrder.items.length > 1 && (
                                            <button 
                                                type="button" 
                                                onClick={() => handleRemoveItem(index)}
                                                className="text-red-500 hover:text-red-700 font-bold transition-colors text-lg"
                                                aria-label="Remove item row"
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Footer buttons */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={() => setIsEditorOpen(false)}
                            className="px-5 py-2.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={printingSingle}
                            className="px-6 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center gap-1.5 shadow-sm"
                        >
                            {printingSingle && <Spinner />}
                            <span>Preview / Print</span>
                        </button>
                    </div>
                </form>
            </ShowModal>

            {/* ═══════════════════════════════════════ */}
            {/* MODAL 2: BATCH REVIEW MODAL            */}
            {/* ═══════════════════════════════════════ */}
            <ShowModal
                isOpen={isBatchOpen}
                onRequestClose={() => setIsBatchOpen(false)}
                title="Review & Edit Batch Labels"
                modalWidth="70%"
            >
                <div className="space-y-6 pb-2 text-left animate-fadeIn">
                    <p className="text-xs text-gray-500 bg-gray-50 p-2.5 rounded-lg border border-gray-200/50">
                        Customize packaging details, per-item notes, and dispatch instructions for each parcel in the batch. You can also duplicate rows to split shipments into multiple parcels.
                    </p>

                    <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                        {batchOrders.map((order, orderIndex) => {
                            const isExpanded = batchExpandedIndex === orderIndex;
                            return (
                                <div 
                                    key={order.tempId || orderIndex} 
                                    className="border border-slate-200/70 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow"
                                >
                                    {/* Accordion Header */}
                                    <div 
                                        onClick={() => setBatchExpandedIndex(isExpanded ? -1 : orderIndex)}
                                        className="flex justify-between items-center bg-slate-50 px-4 py-3 cursor-pointer select-none border-b border-slate-100 hover:bg-slate-100/60 transition-colors"
                                    >
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-xs font-mono font-bold bg-slate-200 text-slate-700 px-2 py-0.5 rounded-md">
                                                Parcel #{orderIndex + 1}
                                            </span>
                                            <span className="font-semibold text-gray-800 text-sm">{order.customer_name || 'Walk-in Buyer'}</span>
                                            {order.shop_name && (
                                                <span className="text-xs text-slate-500 bg-slate-200/50 px-2 py-0.5 rounded font-medium">
                                                    {order.shop_name}
                                                </span>
                                            )}
                                            {order.order_id ? (
                                                <span className="text-[10px] text-blue-600 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded font-mono">
                                                    Order: {order.order_id.slice(0, 8)}...
                                                </span>
                                            ) : (
                                                <span className="text-[10px] text-purple-600 bg-purple-50 border border-purple-100 px-1.5 py-0.5 rounded font-mono">
                                                    Custom Label
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                                            <button 
                                                type="button" 
                                                onClick={(e) => handleDuplicateBatchOrder(orderIndex, e)} 
                                                className="text-blue-600 hover:text-blue-800 text-xs font-bold flex items-center gap-1 bg-white hover:bg-blue-50 px-2.5 py-1 border border-blue-200 rounded-lg transition-colors"
                                            >
                                                ➕ Duplicate
                                            </button>
                                            <button 
                                                type="button" 
                                                onClick={(e) => handleRemoveBatchOrder(orderIndex, e)} 
                                                className="text-red-600 hover:text-red-800 text-xs font-bold flex items-center gap-1 bg-white hover:bg-red-50 px-2.5 py-1 border border-red-200 rounded-lg transition-colors"
                                            >
                                                🗑 Remove
                                            </button>
                                            <span className="text-gray-400 font-bold ml-1 text-xs">
                                                {isExpanded ? '▲' : '▼'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Accordion Content */}
                                    {isExpanded && (
                                        <div className="p-4 space-y-4 border-t border-slate-100 bg-white">
                                            {/* Details inputs */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-xs font-semibold text-gray-500">Customer Name</span>
                                                    <input 
                                                        type="text" 
                                                        value={order.customer_name} 
                                                        onChange={(e) => handleBatchFieldChange(orderIndex, 'customer_name', e.target.value)}
                                                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm" 
                                                    />
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-xs font-semibold text-gray-500">Shop Name</span>
                                                    <input 
                                                        type="text" 
                                                        value={order.shop_name} 
                                                        onChange={(e) => handleBatchFieldChange(orderIndex, 'shop_name', e.target.value)}
                                                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm" 
                                                    />
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-xs font-semibold text-gray-500">Mobile Number</span>
                                                    <input 
                                                        type="text" 
                                                        value={order.mobile_number} 
                                                        onChange={(e) => handleBatchFieldChange(orderIndex, 'mobile_number', e.target.value)}
                                                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm" 
                                                    />
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-xs font-semibold text-gray-500">Alternate Number</span>
                                                    <input 
                                                        type="text" 
                                                        value={order.alternate_number} 
                                                        onChange={(e) => handleBatchFieldChange(orderIndex, 'alternate_number', e.target.value)}
                                                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm" 
                                                    />
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-xs font-semibold text-gray-500">Transport Name</span>
                                                    <input 
                                                        type="text" 
                                                        value={order.transport_name} 
                                                        onChange={(e) => handleBatchFieldChange(orderIndex, 'transport_name', e.target.value)}
                                                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm" 
                                                    />
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-xs font-semibold text-gray-500">Transport Contact Number</span>
                                                    <input 
                                                        type="text" 
                                                        value={order.transport_number} 
                                                        onChange={(e) => handleBatchFieldChange(orderIndex, 'transport_number', e.target.value)}
                                                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm" 
                                                    />
                                                </div>
                                            </div>

                                            <div className="flex flex-col gap-1 text-left">
                                                <span className="text-xs font-semibold text-gray-500">Note</span>
                                                <input 
                                                    type="text" 
                                                    value={order.note} 
                                                    onChange={(e) => handleBatchFieldChange(orderIndex, 'note', e.target.value)}
                                                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm" 
                                                />
                                            </div>

                                            <div className="flex flex-col gap-1 text-left">
                                                <span className="text-xs font-semibold text-gray-500">Dispatch Instructions</span>
                                                <textarea 
                                                    value={order.dispatch_instruction} 
                                                    onChange={(e) => handleBatchFieldChange(orderIndex, 'dispatch_instruction', e.target.value)}
                                                    rows={1.5}
                                                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm resize-none" 
                                                />
                                            </div>

                                            {/* Items inside batch card */}
                                            <div className="space-y-2 pt-2 text-left">
                                                <div className="flex items-center justify-between border-b border-gray-100 pb-1.5">
                                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Items in parcel</span>
                                                    <button 
                                                        type="button" 
                                                        onClick={() => handleBatchAddItem(orderIndex)}
                                                        className="text-[11px] font-semibold text-purple-600 bg-purple-50 hover:bg-purple-100 px-2 py-1 rounded border border-purple-200 transition-colors"
                                                    >
                                                        + Add Item
                                                    </button>
                                                </div>

                                                <div className="space-y-1.5">
                                                    {order.items.map((item, itemIndex) => (
                                                        <div key={itemIndex} className="grid grid-cols-12 gap-2 items-center">
                                                            <div className="col-span-6">
                                                                <ProductSearchInput 
                                                                    value={item.name} 
                                                                    placeholder="Item name..."
                                                                    onChange={(text) => handleBatchItemChange(orderIndex, itemIndex, 'name', text)}
                                                                    onSelect={(prod) => handleBatchItemChange(orderIndex, itemIndex, 'name', prod.product_name)} 
                                                                />
                                                            </div>
                                                            <div className="col-span-2">
                                                                <input 
                                                                    type="number" 
                                                                    min="1" 
                                                                    value={item.quantity} 
                                                                    onChange={(e) => handleBatchItemChange(orderIndex, itemIndex, 'quantity', e.target.value)}
                                                                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm text-center" 
                                                                />
                                                            </div>
                                                            <div className="col-span-3">
                                                                <input 
                                                                    type="text" 
                                                                    value={item.note} 
                                                                    onChange={(e) => handleBatchItemChange(orderIndex, itemIndex, 'note', e.target.value)}
                                                                    placeholder="e.g. Fragile"
                                                                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" 
                                                                />
                                                            </div>
                                                            <div className="col-span-1 text-center">
                                                                {order.items.length > 1 && (
                                                                    <button 
                                                                        type="button" 
                                                                        onClick={() => handleBatchRemoveItem(orderIndex, itemIndex)}
                                                                        className="text-red-500 hover:text-red-700 font-bold transition-colors text-sm"
                                                                    >
                                                                        ✕
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Footer buttons */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={() => setIsBatchOpen(false)}
                            className="px-5 py-2.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleBatchPrint}
                            disabled={printingBatch}
                            className="px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center gap-1.5 shadow-sm"
                        >
                            {printingBatch && <Spinner />}
                            <span>Generate Batch PDF</span>
                        </button>
                    </div>
                </div>
            </ShowModal>
        </div>
    );
}
